import type { GamePhase } from '@/hooks/useGameEngine'

export type GraphicsQuality = 'high' | 'medium' | 'low'

export interface BoardGraphicsProfile {
  dpr: [number, number]
  frameloop: 'always' | 'demand'
  shadows: boolean
  antialias: boolean
  contactShadows: boolean
  contactShadowResolution: number
  trailParticles: boolean
  worldPointLightIntensity: number
  shadowMapSize: number
  postMoveAnimationSeconds: number
  performanceFallbackDelta: number
  performanceFallbackFrames: number
}

export function boardGraphicsProfile(graphics: GraphicsQuality, _phase: GamePhase): BoardGraphicsProfile {
  const frameloop: BoardGraphicsProfile['frameloop'] = 'demand'
  if (graphics === 'low') {
    return {
      dpr: [0.5, 0.72],
      frameloop,
      shadows: false,
      antialias: false,
      contactShadows: false,
      contactShadowResolution: 64,
      trailParticles: false,
      worldPointLightIntensity: 0,
      shadowMapSize: 256,
      postMoveAnimationSeconds: 0,
      performanceFallbackDelta: Number.POSITIVE_INFINITY,
      performanceFallbackFrames: Number.POSITIVE_INFINITY,
    }
  }
  if (graphics === 'medium') {
    return {
      dpr: [0.62, 0.84],
      frameloop,
      shadows: false,
      antialias: false,
      contactShadows: false,
      contactShadowResolution: 96,
      trailParticles: true,
      worldPointLightIntensity: 0.24,
      shadowMapSize: 512,
      postMoveAnimationSeconds: 0.65,
      performanceFallbackDelta: 1 / 56,
      performanceFallbackFrames: 6,
    }
  }
  return {
    dpr: [0.68, 1],
    frameloop,
    shadows: false,
    antialias: true,
    contactShadows: true,
    contactShadowResolution: 96,
    trailParticles: true,
    worldPointLightIntensity: 0.58,
    shadowMapSize: 256,
    postMoveAnimationSeconds: 1.15,
    performanceFallbackDelta: 1 / 58,
    performanceFallbackFrames: 4,
  }
}

export function lowerGraphicsTier(graphics: GraphicsQuality): GraphicsQuality {
  if (graphics === 'high') return 'medium'
  if (graphics === 'medium') return 'low'
  return 'low'
}

export function motionCappedGraphicsTier(graphics: GraphicsQuality, activeMotion: boolean): GraphicsQuality {
  return activeMotion && graphics !== 'low' ? 'low' : graphics
}

export function updateFallbackPressure(current: number, delta: number, profile: BoardGraphicsProfile): { pressure: number; fallback: boolean } {
  if (!Number.isFinite(profile.performanceFallbackDelta)) return { pressure: 0, fallback: false }
  const spikeLimit = Math.max(0.12, profile.performanceFallbackDelta * 5)
  if (delta >= spikeLimit) return { pressure: 0, fallback: true }
  const next = delta > profile.performanceFallbackDelta ? current + 1 : Math.max(0, current - 0.45)
  if (next >= profile.performanceFallbackFrames) return { pressure: 0, fallback: true }
  return { pressure: next, fallback: false }
}
